import { type MixedDressingDetails } from '@sg/graphql-schema';

import { getNextDressingWeight, getPreviousDressingWeight } from './get-dressing-weight';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Increases the weight of a mixed dressing ingredient based on the provided
 * ingredient ID.
 */
export function increaseDressingWeight(
  params: IncreaseMixedDressingWeightParams,
): MixedDressingDetails[] {
  const { ingredientId, mixedDressingDetails } = params;

  const increaseWeight = changeDressingWeightFactory(ingredientId, 'increase');

  return mixedDressingDetails.map(increaseWeight);
}

/**
 * Decreases the weight of a mixed dressing ingredient based on the provided
 * ingredient ID.
 */
export function decreaseDressingWeight(
  params: IncreaseMixedDressingWeightParams,
): MixedDressingDetails[] {
  const { ingredientId, mixedDressingDetails } = params;

  const decreaseWeight = changeDressingWeightFactory(ingredientId, 'decrease');

  return mixedDressingDetails.map(decreaseWeight);
}

// ─────────────────────────────────────────────────────────────────────────────

function changeDressingWeightFactory(ingredientId: string, action: 'increase' | 'decrease') {
  const shouldIncreasePortions = action === 'increase';

  return function (dressingDetails: MixedDressingDetails): MixedDressingDetails {
    const isTargetDressing = dressingDetails.ingredientId !== ingredientId;

    if (isTargetDressing) {
      return dressingDetails;
    }

    const currentWeight = dressingDetails.weight;
    const updatedWeight = shouldIncreasePortions
      ? getNextDressingWeight(currentWeight)
      : getPreviousDressingWeight(currentWeight);

    return { ...dressingDetails, weight: updatedWeight };
  };
}

// ─── Types ───────────────────────────────────────────────────────────────────

type IncreaseMixedDressingWeightParams = {
  ingredientId: string;
  mixedDressingDetails: MixedDressingDetails[];
};
